/**
 * Hand a file to the browser without racing it. (DR2-3051 #4)
 *
 * ── The bug this replaces ───────────────────────────────────────────────────────────────────
 *
 * The pattern everywhere in this app was:
 *
 *     const url = URL.createObjectURL(blob);
 *     const a = document.createElement('a');
 *     a.href = url; a.download = name;
 *     a.click();
 *     URL.revokeObjectURL(url);   // <- synchronous, and this is the bug
 *
 * `a.click()` STARTS a download; it does not wait for one. Revoking on the very next statement
 * can pull the object URL away before the browser has read it, and Chrome reports that as
 * **"File wasn't available on site."** — the exact message on the report.
 *
 * It is a race, so it fails intermittently, which is why it reads as "sometimes works" and why it
 * is nearly impossible to reproduce on demand. The blob is bigger, the machine is busier, the
 * download directory is on a slow disk — and it loses.
 *
 * Two things fix it, and both matter:
 *
 *  - **Defer the revoke.** The browser gets the whole task queue, not the gap between two
 *    statements. Not leaked: it is still revoked, just later.
 *  - **Put the anchor in the document.** A detached `<a>` works in Chrome and does not in every
 *    browser; appending costs nothing and removes a second source of "sometimes".
 */

/** Long enough for the browser to have taken the blob, short enough not to hold memory. */
const REVOKE_DELAY_MS = 60_000;

/**
 * Trigger a download of `blob` as `filename`.
 *
 * Use this rather than hand-rolling the anchor dance — the failure mode of getting it wrong is
 * intermittent and reads to the user as the file being missing rather than as a bug.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    downloadUrl(url, filename);
  } finally {
    /*
     * Deferred, never synchronous. Also never skipped: a leaked object URL holds the whole blob in
     * memory for the life of the document, and an export page someone leaves open would keep every
     * file they downloaded.
     */
    window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}

/**
 * Trigger a download from a URL that the CALLER owns.
 *
 * Separate from `downloadBlob` because the lifetime differs: here the caller decides when (or
 * whether) to revoke, so this must not revoke anything it did not create.
 */
export function downloadUrl(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  /* In the document, not detached — a detached anchor's click is honoured by Chrome and not by
     every browser, which is one more way a download becomes "sometimes". */
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
  }
}

/** Convenience for the commonest case: text content the caller has already assembled. */
export function downloadTextFile(text: string, filename: string, mimeType = 'text/csv;charset=utf-8'): void {
  downloadBlob(new Blob([text], { type: mimeType }), filename);
}
